import React from 'react';
import BasicModal from './BasicModal';
import ReactMarkdown from 'react-markdown';

interface SimplePersonaModalProps {
  persona: any;
  isOpen: boolean;
  onClose: () => void;
}

const SimplePersonaModal: React.FC<SimplePersonaModalProps> = ({ persona, isOpen, onClose }) => {
  if (!persona) return null;

  // Format cell content helper for persona details - based on formatDetailContent from PersonaTable
  const formatContent = (content: any, field: string): React.ReactNode => {
    const arrayFields = ['roles', 'reports_to', 'team', 'phrases_topics', 'metrics'];
    const copyFields = ['good_personalized_copy', 'bad_personalized_copy'];
  
    if (Array.isArray(content) && arrayFields.includes(field)) {
      // Special handling for phrases_topics and metrics which can have longer text
      if (field === 'phrases_topics' || field === 'metrics') {
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
            {content.map((item, index) => (
              <div
                key={index}
                className="bg-gray-100 text-gray-800 px-2 py-1 sm:px-3 sm:py-2 rounded-md text-sm break-words"
              >
                {item}
              </div>
            ))}
          </div>
        );
      }
      
      // Standard handling for other array fields
      return (
        <div className="flex flex-wrap gap-1 sm:gap-2">
          {content.map((item, index) => (
            <span
              key={index}
              className="bg-gray-100 text-gray-800 px-2 py-0.5 sm:py-1 rounded-full text-sm"
            >
              {item}
            </span>
          ))}
        </div>
      );
    }
  
    if (copyFields.includes(field) && typeof content === 'object' && content !== null) {
      const keyOrder = ['copy_reach', 'copy_conversion', 'copy_engagement'];
      const labelMap: Record<string, string> = {
        copy_reach: 'Initial Hook',
        copy_engagement: 'Nurture Copy',
        copy_conversion: 'Call to Action',
      };
    
      const isGoodCopy = field === 'good_personalized_copy';
      const bgColor = isGoodCopy ? 'bg-gray-50' : 'bg-pink-50';
    
      return (
        <div className="space-y-0">
          {keyOrder.map((key) => (
            <div key={key} className="min-h-[200px] flex flex-col justify-start">
              <h4 className="font-semibold text-sm text-pink-700 mb-0.5">{labelMap[key]}</h4>
              <div
                className={`text-base text-gray-600 rounded-md p-3 h-full min-h-[120px] ${bgColor}`}
              >
                <ReactMarkdown>{String(content[key] || '')}</ReactMarkdown>
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    return typeof content === 'string' ? 
      <div className="whitespace-pre-wrap">
        <ReactMarkdown>{content}</ReactMarkdown>
      </div> : 
      String(content || '');
  };

  // Define sections for the persona details
  const sections = [
    {
      title: "Using this research, you can transform generic copy into specific copy",
      fields: [
        { label: "Generic copy", key: "bad_personalized_copy", type: "copy" },
        { label: "Specific copy", key: "good_personalized_copy", type: "copy" },
      ]
    },
    {
      title: "Role Information",
      fields: [
        { label: "Role Set", key: "role_set_name", type: "text" },
        { label: "Roles", key: "roles", type: "array" },
        { label: "Reports To", key: "reports_to", type: "array" },
        { label: "Team", key: "team", type: "array" }
      ]
    },
    {
      title: "Demographics",
      fields: [
        { label: "Gender Distribution", key: "gender_distribution", type: "text" },
        { label: "Age Distribution", key: "age_distribution", type: "text" },
        { label: "Years of Experience", key: "yoe_distribution", type: "text" },
        { label: "Salary Range", key: "salary_range", type: "text" }
      ]
    },
    {
      title: "Detailed Description",
      fields: [
        { label: "What Are They", key: "what_are_they", type: "text" },
        { label: "How They Contribute", key: "how_they_contribute", type: "text" },
        { label: "Phrases & Topics", key: "phrases_topics", type: "array" },
        { label: "Metrics", key: "metrics", type: "array" }
      ]
    },
    {
      title: "A Day in the Life",
      fields: [
        { label: "Daily Schedule", key: "day_in_the_life_data", type: "text" }
      ]
    }
  ];

  const modalTitle = `${persona.industry} • ${persona.employee_range} • ${persona.role_set_name}`;

  // Special renderer for copy section side by side
  const renderCopySection = (section: any) => {
    const goodCopyField = section.fields.find((f: any) => f.key === 'good_personalized_copy');
    const badCopyField = section.fields.find((f: any) => f.key === 'bad_personalized_copy');
    
    const goodCopy = persona[goodCopyField.key];
    const badCopy = persona[badCopyField.key];
    
    if (!goodCopy && !badCopy) return null;
    
    const keyOrder = ['copy_reach', 'copy_conversion', 'copy_engagement'];
    const labelMap: Record<string, string> = {
      copy_reach: 'Initial Hook',
      copy_engagement: 'Nurture Copy',
      copy_conversion: 'Call to Action',
    };
    
    return (
      <div key={section.title} className="border-b border-gray-200 pb-4 sm:pb-6">
        <h3 className="text-lg sm:text-xl font-semibold text-[#db2777] mb-3 sm:mb-4">{section.title}</h3>
        
        <div className="space-y-10 sm:space-y-8">
          {keyOrder.map((key, index) => (
            <div key={key} className={index > 0 ? "pt-2" : ""}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">     
                <div>
                  <div className="text-sm font-medium text-gray-500 mb-1">Generic copy</div>
                  <div className="text-gray-600 text-base bg-pink-50 p-3 rounded-md min-h-[120px]">
                    <ReactMarkdown>{badCopy && badCopy[key] ? badCopy[key] : "No content available"}</ReactMarkdown>
                  </div>
                </div>
                <div className="mt-4 md:mt-0">
                  <div className="text-sm font-medium text-gray-500 mb-1">Specific copy</div>
                  <div className="text-gray-600 text-base bg-gray-50 p-3 rounded-md min-h-[120px]">
                    <ReactMarkdown>{goodCopy && goodCopy[key] ? goodCopy[key] : "No content available"}</ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Special renderer for what_are_they and how_they_contribute fields side by side
  const renderDetailedDescription = (section: any) => {
    const whatField = section.fields.find((f: any) => f.key === 'what_are_they');
    const howField = section.fields.find((f: any) => f.key === 'how_they_contribute');
    
    const whatContent = persona[whatField.key];
    const howContent = persona[howField.key];
    
    const hasContent = whatContent || howContent;
    
    if (!hasContent) return null;
    
    return (
      <div className="col-span-2 mb-4 sm:mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <h4 className="font-medium text-gray-700 text-base sm:text-lg mb-1 sm:mb-2">{whatField.label}</h4>
            <div className="text-gray-600">
              <ReactMarkdown>{whatContent || "No content available"}</ReactMarkdown>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-700 text-base sm:text-lg mb-1 sm:mb-2">{howField.label}</h4>
            <div className="text-gray-600">
              <ReactMarkdown>{howContent || "No content available"}</ReactMarkdown>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Special renderer for phrases_topics and metrics as full width elements
  const renderPhraseMetricFields = (fields: any[], persona: any) => {
    return (
      <div className="space-y-4 sm:space-y-6 mt-4 sm:mt-6">
        {fields
          .filter(field => field.key === 'phrases_topics' || field.key === 'metrics')
          .map((field) => {
            const value = persona[field.key];
            if (!value && value !== 0) return null;
            
            return (
              <div key={field.key} className="space-y-1 sm:space-y-2">
                <h4 className="font-medium text-gray-700 text-base sm:text-lg">
                  {field.label}
                </h4>
                <div className="text-gray-600 text-base">
                  {formatContent(value, field.key)}
                </div>
              </div>
            );
          })}
      </div>
    );
  };

  return (
    <BasicModal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      fullWidth={true}
      showHeader={false}
    >
      <div className="-m-6 flex flex-col max-h-full">
        <div className="bg-white border-b shadow-sm px-4 py-2 sm:p-6 rounded-t-lg sticky top-0 z-20 w-full">
          <div className="flex justify-between items-center w-full">
            <div className="hidden sm:flex flex-1 justify-start">
              {/* Empty div for flex alignment */}
            </div>
            <div className="flex-1 flex flex-col items-center justify-center">
              <h2 className="text-xl sm:text-2xl font-bold text-center mb-1">Persona Details</h2>
              <div className="hidden sm:flex items-center justify-center gap-2 text-center">
                <span className="text-gray-600 text-sm">{persona.industry}</span>
                <span className="text-gray-600 text-sm">•</span>
                <span className="text-gray-600 text-sm">{persona.employee_range}</span>
                <span className="text-gray-600 text-sm">•</span>
                <span className="text-gray-600 text-sm">{persona.role_set_name}</span>
              </div>
            </div>
          
            <div className="flex-1 flex justify-end">
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 p-1 sm:p-2"
                aria-label="Close"
              >
                <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        <div className="px-4 py-4 sm:p-6 overflow-y-auto">
          <div className="space-y-6 sm:space-y-8">
            {/* Special handling for Copy Strategy section */}
            <div className="pt-2 sm:pt-0">
              {renderCopySection(sections[0])}
            </div>
            
            {/* Handle other sections */}
            {sections.slice(1).map((section) => (
              <div key={section.title} className="border-b border-gray-200 pb-4 sm:pb-6">
                <h3 className="text-lg sm:text-xl font-semibold text-[#db2777] mb-3 sm:mb-4">{section.title}</h3>
                
                {/* For Detailed Description section, use a different layout to ensure alignment */}
                {section.title === "Detailed Description" ? (
                  <div>
                    {/* Render What Are They and How They Contribute side by side */}
                    {renderDetailedDescription(section)}
                    
                    {/* Render phrases_topics and metrics as full width elements */}
                    {renderPhraseMetricFields(section.fields, persona)}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-6">
                    {/* Render regular fields */}
                    {section.fields.filter(field => 
                      // Filter out fields that are handled specially
                      !(field.key === 'what_are_they' || field.key === 'how_they_contribute')
                    ).map((field) => {
                      const value = persona[field.key];
                      if (!value && value !== 0) return null;
                      
                      // Make day_in_the_life_data field full width
                      const isFullWidth = field.key === 'day_in_the_life_data';
                        
                      return (
                        <div 
                          key={field.key} 
                          className={`space-y-1 sm:space-y-2 ${isFullWidth ? 'col-span-1 md:col-span-2' : ''}`}
                        >
                          <h4 className="font-medium text-gray-700 text-base sm:text-lg">
                            {field.label}
                          </h4>
                          <div className="text-gray-600 text-base">
                            {formatContent(value, field.key)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </BasicModal>
  );
};

export default SimplePersonaModal; 